import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { Check } from "lucide-react";
import {
  api,
  formatBytes,
  type BoardSummary,
  type ImageAsset,
  type ImageOrigin,
} from "../api";
import { AsyncButton, EmptyState, ErrorNote, Modal, Skeleton, describeError } from "./ui";

const SOURCES: { value: ImageOrigin | ""; label: string }[] = [
  { value: "", label: "All sources" },
  { value: "generated", label: "Generated" },
  { value: "uploaded", label: "Uploaded" },
];

const SKELETON_CELLS = [0, 1, 2, 3, 4, 5, 6, 7];

interface LibraryPickerProps {
  /** How many more images the composer can take. The picker stops selecting at this. */
  room: number;
  /** Already attached; shown, but not selectable twice. */
  attachedIds: ReadonlySet<string>;
  /** Types and size the chosen model accepts. An image outside them is shown but disabled. */
  acceptedMimeTypes: string[];
  maxBytes: number;
  onPick: (images: ImageAsset[]) => void;
  onClose: () => void;
}

/**
 * Choose existing library images as references, so nothing already uploaded is uploaded again.
 * Filterable by board (what a board made or was given) and by source (generated or uploaded).
 */
export function LibraryPicker({
  room,
  attachedIds,
  acceptedMimeTypes,
  maxBytes,
  onPick,
  onClose,
}: LibraryPickerProps): ReactElement {
  const [boards, setBoards] = useState<BoardSummary[]>([]);
  const [boardId, setBoardId] = useState("");
  const [origin, setOrigin] = useState<ImageOrigin | "">("");

  const [images, setImages] = useState<ImageAsset[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);

  // Kept as the assets themselves, in the order they were ticked, so that order is the order
  // they are attached in and a selection survives a filter change.
  const [chosen, setChosen] = useState<ImageAsset[]>([]);

  useEffect(() => {
    // No boards just means the board filter offers nothing; not worth an error over the grid.
    api
      .listBoards()
      .then((page) => setBoards(page.items))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setImages(null);
    setError(null);
    api
      .listImages(
        { board_id: boardId || undefined, origin: origin || undefined },
        controller.signal,
      )
      .then((page) => {
        setImages(page.items);
        setNextCursor(page.next_cursor);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(cause);
        setImages([]);
      });
    return () => controller.abort();
  }, [boardId, origin]);

  async function loadMore(): Promise<void> {
    if (!nextCursor) return;
    try {
      const page = await api.listImages({
        cursor: nextCursor,
        board_id: boardId || undefined,
        origin: origin || undefined,
      });
      setImages((current) => [...(current ?? []), ...page.items]);
      setNextCursor(page.next_cursor);
    } catch (cause) {
      setError(cause);
    }
  }

  function unusableReason(image: ImageAsset): string | undefined {
    if (attachedIds.has(image.id)) return "Already attached.";
    if (acceptedMimeTypes.length > 0 && !acceptedMimeTypes.includes(image.content_type)) {
      return `This model does not take ${image.content_type}.`;
    }
    if (maxBytes > 0 && image.byte_size > maxBytes) {
      return `Larger than this model's ${formatBytes(maxBytes)} limit.`;
    }
    return undefined;
  }

  function toggle(image: ImageAsset): void {
    setChosen((current) => {
      if (current.some((item) => item.id === image.id)) {
        return current.filter((item) => item.id !== image.id);
      }
      return current.length >= room ? current : [...current, image];
    });
  }

  const failure = error ? describeError(error) : null;
  const full = chosen.length >= room;

  return (
    <Modal
      wide
      title="Choose from library"
      onClose={onClose}
      footer={
        <>
          <span className="stamp grow">
            {chosen.length} of {room} selected
          </span>
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <AsyncButton
            className="btn primary"
            disabled={chosen.length === 0}
            disabledReason="Select at least one image."
            onClick={async () => onPick(chosen)}
          >
            {chosen.length > 1 ? `Add ${chosen.length} images` : "Add image"}
          </AsyncButton>
        </>
      }
    >
      <div className="toolbar">
        <select
          className="select"
          style={{ width: "auto", maxWidth: "100%" }}
          aria-label="Filter by board"
          value={boardId}
          onChange={(event) => setBoardId(event.target.value)}
        >
          <option value="">All boards</option>
          {boards.map((board) => (
            <option key={board.id} value={board.id}>
              {board.title}
            </option>
          ))}
        </select>

        <select
          className="select"
          style={{ width: "auto", maxWidth: "100%" }}
          aria-label="Filter by source"
          value={origin}
          onChange={(event) => setOrigin(event.target.value as ImageOrigin | "")}
        >
          {SOURCES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {failure && <ErrorNote title={failure.title} detail={failure.detail} />}

      {images === null && (
        <div className="picker-grid">
          {SKELETON_CELLS.map((index) => (
            <Skeleton key={index} height={112} />
          ))}
        </div>
      )}

      {images !== null && images.length === 0 && !failure && (
        <EmptyState
          title="Nothing matches those filters."
          hint="Widen the board or source, or upload an image instead."
        />
      )}

      {images !== null && images.length > 0 && (
        <>
          <div className="picker-grid" role="group" aria-label="Library images">
            {images.map((image) => {
              const selected = chosen.some((item) => item.id === image.id);
              const reason =
                unusableReason(image) ?? (full && !selected ? "Limit reached." : undefined);
              return (
                <button
                  key={image.id}
                  type="button"
                  className={selected ? "picker-cell is-selected" : "picker-cell"}
                  aria-pressed={selected}
                  disabled={reason !== undefined}
                  title={reason ?? image.prompt ?? "Uploaded image"}
                  onClick={() => toggle(image)}
                >
                  <img
                    src={image.thumb_url ?? image.content_url}
                    alt={image.prompt ?? "Uploaded image"}
                    loading="lazy"
                  />
                  {selected && (
                    <span className="picker-check" aria-hidden>
                      <Check size={12} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {nextCursor && (
            <div className="row" style={{ justifyContent: "center", marginTop: "var(--space-4)" }}>
              <AsyncButton onClick={loadMore}>Load more</AsyncButton>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
